import React, { FC, useEffect, useState } from "react";
import { LmsLoanCommentsTable } from "../ui/LmsSharedTables";
import LmsTable, { ITableActionCustomButton } from "../ui/LmsTable";
import {
  currencyFormatter,
  getDropdownSemanticOptionsFromData,
  useFetchData,
  useParseRouteActions,
} from "../../../helpers/GlobalHelper";
import LmsFormGenerator, { BCFormController } from "../ui/LmsFormGenerator";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import { LmsEndpoints } from "../../constants/LmsEndpoints";
import { guid, isGuid, isNumber } from "../../../Helpers";
import BCModal from "../../ui/BCModal";
import { AddNewBuiltInAmendmentForm } from "../Loans/LmsLoanDetails";
import { WidthType } from "../../interfaces/Global";
import { Confirm } from "semantic-ui-react";

type FormType = "add" | "edit";
type GenericProps = Record<string, unknown>;
type GetterFn = (data, options?) => GenericProps | Promise<GenericProps>;
interface ModalContentProps extends GenericProps {
  handleCloseModal: () => void;
}

interface LoanExtensionFormProps {
  extensionId?: string;
  loanId: string | null;
  type: FormType;
}

enum ModalContentName {
  EditPropertyUnit = "EditPropertyUnit",
  EditPropertyBudget = "EditPropertyBudget",
}

const EXTENSION_TYPE_ID = {
  MODIFICATION: "3ad31248-d9b6-4105-9aed-a65200c1dc37",
  EXTENSION: "a184ce7c-854e-4509-8348-a64900ce7935",
};

const STATUS_IDS_DISABLE_FORM = [
  "44f2a3f0-12a7-4bf4-b6db-ad7d00524386",
  "106f4161-440f-44ec-a467-a69000318927",
  "e95b6512-1f61-4fcb-84c4-ad7d005068ab",
];

const CREATE_AMENDMENT_MSG =
  "Your data will be saved and an Amendment will be created. Do you wish to continue?";

const ADD_SAVE_BTN_CLASS_NAME = "add-save-btn";
const LOAN_ID_PARAM = "loanId";
const OPEN_MODAL_EVENT_NAME = "openLoanExtensionModal";
const ROW_PREFIX = "budget_row-";
const HELP_AMENDMENT_BUTTON = {
  text: "Help",
  icon: GlobalConstants.Icons.QUESTION,
  handleClick: () => null,
};

const DECLINE_AMENDMENT_BUTTON = {
  method: "PUT",
  text: "Decline",
  action: "submitselected",
  refreshDataOnSuccess: true,
  submitSuffix: "amendments/decline",
  confirmationMessage: "Are you sure you want to decline the selected extension(s)",
  isDisabled: (checkedItems, data) => {
    const item = checkedItems.length === 1 ? data[checkedItems[0]] : null;
    return (
      item === null ||
      ["declined", "implemented"].includes(item.extensionStatus.toLowerCase()) ||
      item.loanSubStatus === "Lost"
    );
  },
} as ITableActionCustomButton;

const MODIFICATION_FEE_BASED_OPTIONS = [
  "Loan Amount Change",
  "Flat Fee",
  "New Committed Loan Amount",
];

enum CHANGING_SECTIONS {
  TERM = "isTermChanging",
  RATE = "isRateChanging",
  BUDGET = "isBudgetChanging",
  INTEREST = "isInterestReserveChanging",
}

enum EXTENSION_DROPDOWN_ENDPOINTS {
  DECISIONS = "/metadata/list-extension-decisions",
  STATUSES = "/metadata/list-extension-statuses",
  TYPES = "/metadata/list-extension-types",
  SPE = "orgs/list-spes",
  BORROWER = "metadata/list-borrower-organizations",
  SERVICING_LOANS = "metadata/list-servicing-loans",
}

export const appendParamsToUrl = (url: string, params: GenericProps): string => {
  const queryParams = new URLSearchParams();
  for (const name in params) {
    const value = params[name];
    if (!(value === undefined || value === null)) queryParams.append(name, value as string);
  }

  return `${url}?${queryParams}`;
};

const dispatchOpenModal = (formType: FormType, modalName: ModalContentName): void => {
  const event = new CustomEvent(OPEN_MODAL_EVENT_NAME, { detail: { formType, modalName } });
  window.dispatchEvent(event);
};

const getSumByReduce = (...values): number => values.reduce((sum, value) => sum + (+value || 0), 0);

const getterPipe =
  (...fns: GetterFn[]): GetterFn =>
  async (initialData, options) =>
    (
      await fns.reduce(async (acc, fn) => {
        const { data, changedData } = await acc;
        const fnChangedData = await fn(data, options);
        const newChangedData = { ...changedData, ...fnChangedData };
        if (FeatureFlags.IS_DEBUG && fn.name) {
          console.log(`pipe fn: ${fn.name}`, fnChangedData);
        }
        return {
          changedData: newChangedData,
          data: { ...data, ...newChangedData },
        };
      }, Promise.resolve({ data: initialData, changedData: {} }))
    ).changedData;

const isSectionHidden =
  (section: CHANGING_SECTIONS) =>
  ({ inputs }) =>
    !inputs[section];

const [isTermHidden, isRateHidden, isBudgetHidden, isInterestHidden] = [
  CHANGING_SECTIONS.TERM,
  CHANGING_SECTIONS.RATE,
  CHANGING_SECTIONS.BUDGET,
  CHANGING_SECTIONS.INTEREST,
].map((section) => isSectionHidden(section));

const isAmendedDetailsHidden = ({ inputs }) =>
  !(inputs[CHANGING_SECTIONS.INTEREST] || inputs[CHANGING_SECTIONS.BUDGET]);

const isRiskMsgHidden = ({ inputs }) => !inputs.riskMessage;
const setRiskMessage = (myForm: BCFormController) => {
  myForm.addCenteredGroup(
    [
      myForm.getComponent(({ children }) => <h5 style={{ color: "red" }}>{children}</h5>, {
        name: "riskMessage",
        width: 16,
      }),
    ],
    {
      isHiddenFunc: isRiskMsgHidden,
    }
  );
};

const getFeeBasedOffOptions: GetterFn = (data) => {
  const modificationFeeBasedOptions = data.extensionTypeId === EXTENSION_TYPE_ID.MODIFICATION;
  return {
    modificationFeeBasedOptions,
    feeBasedOffOption:
      (!modificationFeeBasedOptions &&
        MODIFICATION_FEE_BASED_OPTIONS.includes(data.feeBasedOffOption)) ||
      !data.feeBasedOffOption
        ? "Original Committed Loan Amount"
        : data.feeBasedOffOption,
  };
};

const getAmendedDetails: GetterFn = async (data, options) => {
  const { setIsFormLoading } = options;
  setIsFormLoading(true);
  const response = await options.fetchData(
    appendParamsToUrl("/loans/amendments/extension-details", {
      loanId: data.loanId,
      loanExtensionId: data.formType === "add" ? null : data.id,
      newExtensionStatusId: data.extensionStatusId,
      extensionTypeId: data.extensionTypeId,
      budgetChangeAmount: data.budgetChangeAmount,
      extensionDate: data.extensionDate,
      extensionDays: data.extensionDays,
    })
  );

  setIsFormLoading(false);
  if (response.data) {
    const { enableLrc, riskLevel, riskMessage, changeDecision } = response.data;
    return {
      enableLrc,
      riskLevel,
      riskMessage,
      changeDecision,
    };
  }

  return {};
};

const getExtensionType: GetterFn = (data) => {
  const extensionTypeName =
    data.isBudgetChanging ||
    data.isInterestReserveChanging ||
    (data.isRateChanging && !data.isTermChanging) ||
    (data.isTermChanging && data.extensionDays > 90)
      ? "MODIFICATION"
      : "EXTENSION";
  const newExtensionTypeId = EXTENSION_TYPE_ID[extensionTypeName];

  return [
    "executedLoanAmendmentAgreement",
    "firstAmendmentRecorded",
    "lmsupdated",
    "titleUpdated",
    "budgetUpdated",
  ].reduce(
    (acc, key) => ({
      ...acc,
      [key]: extensionTypeName === "MODIFICATION" ? false : data[key],
    }),
    {
      extensionTypeName,
      extensionTypeId: newExtensionTypeId,
      prorateFeeAtPayoff: extensionTypeName === "EXTENSION",
      paymentMethodOption: extensionTypeName === "EXTENSION" ? "Borrower Pays" : "Paid at Payoff",
    }
  );
};

const getExtensionTypePipe: GetterFn = getterPipe(
  getExtensionType,
  getFeeBasedOffOptions,
  getAmendedDetails
);

const getOverBy: GetterFn = (data) => {
  const [currentLoanPlusFee, amendedLoanAmount] = [
    data.currentLoanPlusFee,
    data.amendedLoanAmount,
  ].map((value) => value || 0);

  const diff = currentLoanPlusFee - amendedLoanAmount;
  return {
    overBy: diff > 0 ? diff : 0,
  };
};

const getCurrentLoanPlusFee: GetterFn = (data) => ({
  currentLoanPlusFees: getSumByReduce(
    data.loanAmountCurrent,
    data.otherFees,
    data.extensionFeeAmount
  ),
});

const getCurrentLoanPlusFeePipe: GetterFn = getterPipe(getCurrentLoanPlusFee, getOverBy);

const getExtensionFeeEffectiveDate: GetterFn = (data) => {
  const effectiveDate = [
    [data[CHANGING_SECTIONS.TERM], data.extensionDate],
    [data[CHANGING_SECTIONS.RATE], data.rateChangeEffectiveDate],
    [data[CHANGING_SECTIONS.BUDGET], data.budgetChangeEffectiveDate],
    [data[CHANGING_SECTIONS.INTEREST], data.interestReserveChangeEffectiveDate],
  ].reduce(
    (effectiveDate, [isSectionActive, date]) =>
      isSectionActive && date
        ? !effectiveDate
          ? date
          : Date.parse(effectiveDate) > Date.parse(date)
          ? date
          : effectiveDate
        : effectiveDate,
    ""
  );

  return {
    requestedExtensionFeeEffectiveDate: effectiveDate,
    extensionFeeEffectiveDate: effectiveDate,
  };
};

const getLoanExtensionFee: GetterFn = (data) => {
  const [extensionFeePercentage, loanAmountOriginal, loanAmountCurrent, amendedLoanAmount] = [
    data.extensionFeePercentage,
    data.loanAmountOriginal,
    data.loanAmountCurrent,
    data.amendedLoanAmount,
  ].map((value) => value || 0);
  let extensionFeeAmount: number | null = null;
  switch (data.feeBasedOffOption) {
    case "Original Committed Loan Amount":
      extensionFeeAmount = (extensionFeePercentage * loanAmountOriginal) / 100;
      break;
    case "Current Balance":
      extensionFeeAmount = (extensionFeePercentage * loanAmountCurrent) / 100;
      break;
    case "Loan Amount Change":
      extensionFeeAmount =
        (extensionFeePercentage * (amendedLoanAmount - loanAmountOriginal)) / 100;
      break;
    case "New Committed Loan Amount":
      extensionFeeAmount = (extensionFeePercentage * amendedLoanAmount) / 100;
  }

  return { extensionFeeAmount };
};

const getRequiredFeeDates: GetterFn = (data) => {
  const hasFeeValues = !!(data.extensionFeeAmount || data.extensionFeePercentage);
  return {
    requiredExtensionFeeEffectiveDate: hasFeeValues,
    requiredRequestedExtensionFeeEffectiveDate: hasFeeValues,
  };
};

const getChangeExtensionFeePercentagePipe: GetterFn = getterPipe(
  getLoanExtensionFee,
  getCurrentLoanPlusFeePipe,
  getRequiredFeeDates
);

const getChangeExtensionFeeAmount: GetterFn = () => ({
  extensionFeePercentage: null,
});

const getChangeExtensionFeeAmountPipe: GetterFn = getterPipe(
  getChangeExtensionFeeAmount,
  getCurrentLoanPlusFeePipe,
  getRequiredFeeDates
);

const getChangeFeeBasedOffOption: GetterFn = (data, options) => {
  const extensionFeePercentage = data.extensionFeePercentage || 0;
  return data.feeBasedOffOption === "Flat Fee" && data.extensionFeeAmount
    ? getChangeExtensionFeeAmountPipe(data, options)
    : getChangeExtensionFeePercentagePipe({ ...data, extensionFeePercentage }, options);
};

const getCalculatedAmendedLoanAmount: GetterFn = (data) => {
  const { unitsCount, budgetChangeAmount, interestReserveChangeAmount } = data;
  const amendedLoanAmount = getSumByReduce(
    budgetChangeAmount,
    data.loanAmountOriginal,
    interestReserveChangeAmount
  );

  const nthAmendedLoanAmount = getSumByReduce(
    data.nthAmendedLoanAmount,
    budgetChangeAmount,
    interestReserveChangeAmount
  );

  const amendedLTVValue = (amendedLoanAmount * 100) / data.underwrittenValue;

  return {
    amendedLoanAmount,
    nthAmendedLoanAmount,
    amendedLoanToValue:
      data.underwrittenValue === 0 ? 0 : amendedLTVValue > 9999.99 ? 9999.99 : amendedLTVValue,
    ...(unitsCount > 0
      ? {
          recommendedDeedReleaseAmount: amendedLoanAmount / unitsCount,
          approvedPartialDeedReleaseAmount: amendedLoanAmount / unitsCount,
        }
      : {}),
  };
};

const getLTVMessage: GetterFn = async (data, options) => {
  const { setIsFormLoading } = options;
  setIsFormLoading(true);
  const response = await options.fetchData("/amendments/ltv-message", {
    data: {
      amendedLoanToValue: data.amendedLoanToValue,
      loanId: data.loanId,
    },
    fetchMethod: "POST",
  });

  setIsFormLoading(false);
  return {
    amendedLtvMessage:
      response.data && response.data.amendedLtvMessage ? response.data.amendedLtvMessage : "",
  };
};

const getCalculatedAmendedLoanAmountPipe: GetterFn = getterPipe(
  getCalculatedAmendedLoanAmount,
  getLTVMessage,
  getOverBy
);

const setIsTermChangingFields = (myForm: BCFormController): void => {
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Term Changing", {
        name: CHANGING_SECTIONS.TERM,
        field: "checkbox",
        inlineRight: true,
      }),
    ],
    { widths: "4" }
  );

  myForm.addGroupFields(
    [
      myForm.getCalendarField("Submission Date", { disabled: true }),
      myForm.getCalendarField("Current Maturity", {
        disabled: true,
        keepHiddenPosition: true,
        isHiddenFunc: ({ inputs }) => !inputs.maturityDateOriginal,
      }),
      myForm.getCalendarField("Implemented On", { disabled: true }),
      myForm.getNumberField("Amendment Days", {
        name: "extensionDays",
        required: true,
        isDisabledOnFunc: ({ inputs }) => !!inputs.implementedOn,
      }),
    ],
    { isHiddenFunc: isTermHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getCalendarField("Proposed Maturity Date", { disabled: true }),
      myForm.getCalendarField("Amendment Date", {
        name: "extensionDate",
        required: true,
        isDisabledOnFunc: ({ inputs }) => !!inputs.extensionDate,
      }),
    ],
    { widths: 4, isHiddenFunc: isTermHidden }
  );
};

const getProposedDate: GetterFn = (data) => {
  const extensionDays = +data.extensionDays || 0;
  if (data.maturityDateOriginal) {
    const proposedDate = new Date(data.extensionDate);
    proposedDate.setDate(
      proposedDate.getDate() + ((data.sumPreviousExtensionDays || 0) + extensionDays - 1)
    );

    return {
      proposedMaturityDate: proposedDate.toISOString(),
      extensionFeePercentage:
        extensionDays === 30 ? 0.75 : extensionDays === 60 ? 1.5 : data.extensionFeePercentage,
    };
  }
  return {};
};

const getProposedDatePipe: GetterFn = getterPipe(getProposedDate, getExtensionTypePipe);

const handleTermChanging: GetterFn = (data) =>
  !data[CHANGING_SECTIONS.TERM]
    ? {
        extensionDays: 0,
      }
    : {};

const handleTermChangingPipe: GetterFn = getterPipe(
  handleTermChanging,
  getExtensionTypePipe,
  getExtensionFeeEffectiveDate,
  getProposedDate
);

const setIsRateChangingFields = (myForm: BCFormController): void => {
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Rate Changing", {
        name: CHANGING_SECTIONS.RATE,
        field: "checkbox",
        inlineRight: true,
      }),
    ],
    { widths: "4" }
  );
  myForm.addGroupFields(
    [
      myForm.getDisplayField("Original Interest Rate", {
        fieldType: "float",
        name: "interestRateOriginal",
      }),
      myForm.getDisplayField("Current Interest Rate", {
        fieldType: "float",
        name: "interestRateCurrent",
      }),
      myForm.getCalendarField("Rate Change Implemented On", { disabled: true }),
      myForm.getBlankField(),
    ],
    { isHiddenFunc: isRateHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Rate Change %", {
        disabled: true,
        fieldType: "percent",
        allowNegativeValue: true,
        name: "extensionRateChangePercentage",
      }),
      myForm.getInputField("Revised Rate", {
        required: true,
        fieldType: "float",
        name: "newInterestRate",
      }),
      myForm.getCalendarField("Requested Effective Rate Change", {
        required: true,
        name: "requestedRateChangeEffectiveDate",
      }),
      myForm.getCalendarField("Effective Rate Change", {
        required: true,
        name: "rateChangeEffectiveDate",
      }),
    ],
    { isHiddenFunc: isRateHidden }
  );
};

const getRateChangeDates: GetterFn = (data) => ({
  rateChangeEffectiveDate: data.requestedRateChangeEffectiveDate,
});

const getRateChangeDatesPipe: GetterFn = getterPipe(
  getRateChangeDates,
  getExtensionFeeEffectiveDate
);

const getNewInterestRate: GetterFn = (data) => {
  const { interestRateCurrent, newInterestRate } = data;
  return {
    extensionRateChangePercentage: newInterestRate
      ? isNumber(interestRateCurrent)
        ? +newInterestRate - interestRateCurrent
        : newInterestRate
      : null,
  };
};

const getNewInterestRatePipe: GetterFn = getterPipe(getNewInterestRate, getExtensionTypePipe);

const handleRateChanging: GetterFn = (data) =>
  !data[CHANGING_SECTIONS.TERM]
    ? {
        newInterestRate: null,
        rateChangeEffectiveDate: null,
        requestedRateChangeEffectiveDate: null,
      }
    : {};

const handleRateChangingPipe: GetterFn = getterPipe(
  handleRateChanging,
  getExtensionTypePipe,
  getNewInterestRatePipe,
  getExtensionFeeEffectiveDate
);

const usePropertyBudget = (loanExtensionId: string) => {
  const propertiesUrl = `amendments/${loanExtensionId}/properties`;
  const [{ data: properties = [], isLoading, error }] = useFetchData(propertiesUrl);
  return { properties, isLoading, error, propertiesUrl };
};

const EditPropertyBudget = ({
  loanExtensionId,
  budgetChangeAmount,
  handleCloseModal,
  onSuccess,
}) => {
  const { properties, propertiesUrl, isLoading, error } = usePropertyBudget(loanExtensionId);
  const leftToAllocate = properties.reduce(
    (diff, prop) => diff - prop.changeInBudgetAmount,
    budgetChangeAmount || 0
  );

  const form = new BCFormController(
    {
      leftToAllocate,
      budgetChangeAmount,
    },
    {
      submitSuffix: "/amendments/properties",
      isCompact: true,
      submitMethod: "PATCH",
      clearSuffixesOnSubmit: [propertiesUrl],
      dataFormatterFunc: (data) => {
        const formattedData: unknown[] = [];
        for (const prop in data) {
          if (isGuid(prop)) {
            const originalBudgetItem = properties.find((bud) => bud.id === prop);
            const budgetItem = data[prop];
            const changeInBudgetAmount = budgetItem[`${prop}_changeInBudgetAmount`];
            if (originalBudgetItem.changeInBudgetAmount !== changeInBudgetAmount) {
              formattedData.push({
                id: prop,
                changeInBudgetAmount,
                newBudgetAmount: budgetItem.newBudgetAmount,
              });
            }
          }
        }
        return formattedData;
      },
    }
  );

  form.addGroupFields([
    form.getCurrencyField("Modify Budget", {
      disabled: true,
      name: "budgetChangeAmount",
    }),
    form.getCurrencyField("Left To Allocate", { disabled: true }),
    form.getCheckboxField("Hide Paid Off Properties", { position: "center" }),
  ]);

  if (properties.length > 0) {
    form.addGroupFields(
      [
        ...(
          [
            ["Property", 3],
            ["Lot", 4],
            ["Budget Amount", 3],
            ["Modify Budget by", 3, true],
            ["New Budget", 3],
            ["All Units Paid", 3],
          ] as [string, WidthType, boolean][]
        ).map(([label, width, required]) =>
          form.getLabelField(label, { position: "center", width, required })
        ),
      ],
      { widths: 16 }
    );

    properties.forEach((property) => {
      form.addGroupFields([
        form.getLabelField(property.loanProperty.display, {
          width: 3,
          position: "center",
          noBackground: true,
          wrapLabel: true,
          fontWeightLight: true,
        }),
        form.getLabelField(property.loanProperty.lotNumber, {
          width: 4,
          position: "center",
          noBackground: true,
          wrapLabel: true,
          fontWeightLight: true,
        }),
        form.getCurrencyField("Budget Amount", {
          label: "",
          width: 3,
          value: property.budgetAmount,
          name: "budget",
          disabled: true,
          calculatedTargetField: "totalBudgetAmount",
          nestedInputsProp: property.id,
        }),
        form.getCurrencyField("Modify Budget by", {
          label: "",
          width: 3,
          nestedInputsProp: property.id,
          name: `${property.id}_changeInBudgetAmount`,
          value: property.changeInBudgetAmount,
          calculatedTargetField: "totalModifyByAmount",
        }),
        form.getCurrencyField("New Budget", {
          label: "",
          width: 3,
          disabled: true,
          name: "newBudgetAmount",
          nestedInputsProp: property.id,
          value: property.budgetAmount + property.changeInBudgetAmount,
          calculatedTargetField: "totalNewBudgetAmount",
        }),
        form.getCheckboxField("All Units Paid", {
          label: "",
          width: 3,
          value: property.allUnitsPaid,
          disabled: true,
          position: "center",
          noBackground: true,
        }),
      ]);
    });

    form.addGroupFields([
      form.getBlankField({ width: 7 }),
      form.getCurrencyField("totalBudgetAmount", {
        label: "",
        width: 3,
        value: 0,
        disabled: true,
        noBackground: true,
      }),
      form.getCurrencyField("totalModifyByAmount", {
        label: "",
        width: 3,
        value: 0,
        disabled: true,
        noBackground: true,
      }),
      form.getCurrencyField("totalNewBudgetAmount", {
        label: "",
        width: 3,
        value: 0,
        disabled: true,
        noBackground: true,
      }),
      form.getBlankField({ width: 3 }),
    ]);
  }

  const onFormChange = (options, field) => {
    if (field.includes("changeInBudgetAmount")) {
      const id = field.split("_")[0];
      const row = { ...options.inputs[id] };
      row.newBudgetAmount = row.budget + +row[field];
      options.setInputs((prevInputs) => ({
        ...prevInputs,
        [id]: row,
        leftToAllocate: prevInputs.budgetChangeAmount - prevInputs.totalModifyByAmount,
        totalNewBudgetAmount: prevInputs.totalBudgetAmount + prevInputs.totalModifyByAmount,
      }));
    }
  };

  const handleSubmitValidation = async (inputs) => {
    if (inputs.leftToAllocate !== 0) return { errorMessage: "Left To Allocate should be 0." };
    return { success: true };
  };

  useEffect(() => {
    if (leftToAllocate === 0 && properties.length > 0) {
      onSuccess();
    }
  }, [leftToAllocate, properties.length]);

  return (
    <LmsFormGenerator
      myForm={form}
      error={error}
      isLoading={isLoading}
      onSubmitSuccess={onSuccess}
      onFormChanged={onFormChange}
      handleClose={handleCloseModal}
      handleSubmitValidation={handleSubmitValidation}
      actions={[{ action: "save", text: "Next" }, "close", HELP_AMENDMENT_BUTTON]}
    />
  );
};

const toggleActiveRow = (id: string, type: "add" | "remove") => {
  const className = "extension-budget-row-active";
  const row = document.querySelector(`.${ROW_PREFIX}${id}`);

  if (row) {
    if (type === "add") {
      row.classList.add(className);
    } else if (type === "remove") {
      row.classList.remove(className);
    }
  }
};

const CurrencyField = ({ value }) => (
  <div className="font-weight400">
    <label className="lms-form-input-label">{currencyFormatter(value || 0)}</label>
  </div>
);

const DEFAULT_TOTALS = {
  totalOriginalBudget: 0,
  totalAllocatedFunds: 0,
  totalNewBudget: 0,
  totalNewRemaining: 0,
};

const TOTALS_KEYS = Object.keys(DEFAULT_TOTALS);
const getNewBudget = (budget) => getSumByReduce(budget.budgetAmount, budget.changeBudgetAmount);
const getRemaining = (budget) =>
  getSumByReduce(budget.budgetAmount - budget.disbursedAmount || 0, budget.changeBudgetAmount);

const calculateTotals = (budgets) =>
  budgets.reduce(
    (acc, budget) => ({
      totalOriginalBudget: acc.totalOriginalBudget + +budget.budgetAmount,
      totalAllocatedFunds: acc.totalAllocatedFunds + +budget.changeBudgetAmount,
      totalNewBudget: acc.totalOriginalBudget + getNewBudget(budget),
      totalNewRemaining: acc.totalOriginalBudget + getRemaining(budget),
    }),
    DEFAULT_TOTALS
  );

const getBudgetValues = (budget) => ({
  newBudget: getNewBudget(budget),
  remaining: getRemaining(budget),
});

const getBudgetFieldName = (budgetId: string, propertyId: string, name: string): string =>
  `${name}_B(${budgetId})P(${propertyId})`;

const EditPropertyBudgetDetails = ({ loanExtensionId, riskMessage }) => {
  const [budgetsData, setBudgetsData] = useState({});
  const [totals, setTotals] = useState(DEFAULT_TOTALS);
  const [activeProperty, setActiveProperty] = useState<any>({
    id: "",
    changeInBudgetAmount: 0,
  });
  const { properties, isLoading, error } = usePropertyBudget(loanExtensionId);
  const budgetsUrl = `loans/amendments/extension-budgets/${loanExtensionId}`;

  const [{ data: extensionBudgets = [], isLoading: isUnitsLoading, error: unitsError }] =
    useFetchData(budgetsUrl);

  const handleActiveProperty = (property): void => {
    toggleActiveRow(property.id, "add");
    toggleActiveRow(activeProperty.id, "remove");
    setActiveProperty({ ...property });
  };

  const myForm = new BCFormController(
    { riskMessage },
    {
      values: {
        riskMessage,
        activePropertyId: activeProperty.id,
        increase: activeProperty.changeInBudgetAmount,
        leftToAllocate: activeProperty.changeInBudgetAmount - totals.totalAllocatedFunds,
      },
      isCompact: true,
      clearSuffixesOnSubmit: [budgetsUrl],
      submitSuffix: "/loans/amendments/extension-budgets",
      submitMethod: "PUT",
      dataFormatterFunc: (data) => {
        const formattedData: unknown[] = [];
        for (const prop in data) {
          if (isGuid(prop)) {
            const originalBudgetItem = extensionBudgets.find((bud) => bud.id === prop);
            const budgetItem = data[prop];
            formattedData.push({
              ...originalBudgetItem,
              changeBudgetAmount:
                budgetItem[
                  getBudgetFieldName(
                    originalBudgetItem.id,
                    originalBudgetItem.loanExtensionLoanPropertyId,
                    "changeBudgetAmount"
                  )
                ],
            });
          }
        }
        return formattedData;
      },
    }
  );

  if (properties.length > 0) {
    myForm.addCenteredGroup([
      ...(
        [
          ["Property Address", 4],
          ["Lot", 4],
          ["Modify Budget by", 4],
        ] as [string, WidthType][]
      ).map(([label, width]) => myForm.getLabelField(label, { width })),
      myForm.getBlankField({ width: 4 }),
    ]);

    properties.forEach((property) => {
      myForm.addCenteredGroup(
        [
          myForm.getLabelField(property.loanProperty.display, {
            wrapLabel: true,
            fontWeightLight: true,
            width: 4,
          }),
          myForm.getLabelField(property.loanProperty.lotNumber, {
            wrapLabel: true,
            fontWeightLight: true,
            width: 4,
          }),
          myForm.getCurrencyField("", {
            width: 4,
            disabled: true,
            name: "changeInBudgetAmount",
            value: property.changeInBudgetAmount,
            nestedInputsProp: `prop_${property.id}`,
          }),
          myForm.getButtonField("Change Budget Upload", () => null, {
            width: 4,
            fullWidth: true,
            isHiddenFunc: ({ inputs }) => inputs.activePropertyId !== property.id,
          }),
          myForm.getButtonField("Modify Budget", handleActiveProperty.bind(null, property), {
            width: 4,
            fullWidth: true,
            isHiddenFunc: ({ inputs }) => inputs.activePropertyId === property.id,
          }),
        ],
        { groupClassName: `${ROW_PREFIX}${property.id}` }
      );
    });
  }

  setRiskMessage(myForm);
  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getButtonField("Add", () => null, { fullWidth: true, width: 2 }),
    myForm.getBlankField({ width: 5 }),
    myForm.getCurrencyField("Increase", {
      disabled: true,
      required: true,
      width: 3,
    }),
    myForm.getBlankField({ width: 1 }),
    myForm.getCurrencyField("Left To Allocate", { disabled: true, width: 3 }),
    myForm.getBlankField({ width: 2 }),
  ]);

  if (extensionBudgets.length > 0) {
    myForm.addGroupFields([
      ...(
        [
          ["Category", 4],
          ["Sub-Category", 2],
          ["Original Budget", 1],
          ["Remaining", 1],
          ["Work Inspections Percent Complete", 2],
          ["Allocated Funds", 2],
          ["New Budget", 1],
          ["New Remaining", 1],
          ["Comments", 2],
        ] as [string, WidthType][]
      ).map(([label, width]) => myForm.getLabelField(label, { width, wrapLabel: true })),
    ]);

    extensionBudgets.forEach((budget) => {
      const nestedInputsProp = budget.id;
      const remainingAmount = budget.budgetAmount - budget.disbursedAmount || 0;
      const { newBudget, remaining } = getBudgetValues(budget);
      const getFieldName = getBudgetFieldName.bind(
        null,
        budget.id,
        budget.loanExtensionLoanPropertyId
      );

      myForm.addGroupFields(
        [
          myForm.getLabelField(budget.display || "", {
            wrapLabel: true,
            fontWeightLight: true,
            width: 4,
          }),
          myForm.getLabelField(budget.subCategory || "", {
            wrapLabel: true,
            fontWeightLight: true,
            width: 2,
          }),
          myForm.getLabelField(currencyFormatter(budget.budgetAmount || 0), {
            wrapLabel: true,
            fontWeightLight: true,
            width: 1,
          }),
          myForm.getLabelField(currencyFormatter(remainingAmount), {
            wrapLabel: true,
            fontWeightLight: true,
            width: 1,
          }),
          myForm.getLabelField(`${((budget.percentWorkComplete || 0) / 100).toFixed(2)} %`, {
            wrapLabel: true,
            fontWeightLight: true,
            width: 2,
          }),
          myForm.getCurrencyField("", {
            width: 2,
            nestedInputsProp,
            value: budget.changeBudgetAmount,
            name: getFieldName("changeBudgetAmount"),
          }),
          myForm.getCurrencyField("", {
            width: 1,
            disabled: true,
            nestedInputsProp,
            noBackground: true,
            name: "newBudget",
            value: newBudget,
          }),
          myForm.getCurrencyField("", {
            width: 1,
            disabled: true,
            nestedInputsProp,
            noBackground: true,
            name: "remaining",
            value: remaining,
          }),
          myForm.getInputField("", {
            width: 2,
            name: getFieldName("description"),
          }),
        ],
        {
          groupClassName: `${ROW_PREFIX}${budget.id}`,
          isHiddenFunc: ({ inputs }) =>
            inputs.activePropertyId !== budget.loanExtensionLoanPropertyId,
        }
      );
    });

    myForm.addGroupFields([
      myForm.getBlankField({ width: 6 }),
      myForm.getComponent(CurrencyField, {
        width: 1,
        wrapLabel: true,
        fontWeightLight: true,
        name: "totalOriginalBudget",
      }),
      myForm.getBlankField({ width: 3 }),
      myForm.getComponent(CurrencyField, {
        width: 2,
        wrapLabel: true,
        fontWeightLight: true,
        name: "totalAllocatedFunds",
      }),
      myForm.getComponent(CurrencyField, {
        width: 1,
        wrapLabel: true,
        fontWeightLight: true,
        name: "totalNewBudget",
      }),
      myForm.getComponent(CurrencyField, {
        width: 1,
        wrapLabel: true,
        fontWeightLight: true,
        name: "totalNewRemaining",
      }),
      myForm.getBlankField({ width: 2 }),
    ]);
  }

  useEffect(() => {
    if (properties.length > 0) {
      setActiveProperty(properties[0]);
    }
  }, [properties.length]);

  useEffect(() => {
    if (activeProperty.id && extensionBudgets.length > 0) {
      toggleActiveRow(activeProperty.id, "add");
      const formattedBudgets = extensionBudgets.reduce((acc, budget) => {
        const key = budget.loanExtensionLoanPropertyId;
        const prevValues = key in acc ? acc[key] : [];
        return { ...acc, [key]: prevValues.concat([budget]) };
      }, {});

      setBudgetsData(formattedBudgets);
    }
  }, [extensionBudgets.length, activeProperty.id]);

  useEffect(() => {
    if (activeProperty.id) {
      setTotals(
        calculateTotals(activeProperty.id in budgetsData ? budgetsData[activeProperty.id] : [])
      );
    }
  }, [budgetsData, activeProperty.id]);

  const onFormChanged = (options, field) => {
    if (field.includes("changeBudgetAmount")) {
      const result = /B\((.*)\)P\((.*)\)/g.exec(field.split("_")[1]);
      if (result !== null) {
        const [, budgetId, propertyId] = result;
        const inputBudget = options.inputs[budgetId];
        const changeBudgetAmount = +inputBudget[field];

        const newInputBudget = {
          ...inputBudget,
          ...getBudgetValues({
            ...(budgetsData[propertyId].find((bud) => bud.id === budgetId) || {}),
            changeBudgetAmount,
          }),
        };
        setTotals(DEFAULT_TOTALS);
        options.setInputs((prevInputs) => ({
          ...prevInputs,
          [budgetId]: newInputBudget,
        }));
      }
    }
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      error={error || unitsError}
      onFormChanged={onFormChanged}
      isLoading={isLoading || isUnitsLoading}
      componentProps={{
        riskMessage: {
          children: riskMessage,
        },
        ...TOTALS_KEYS.reduce((acc, key) => ({ ...acc, [key]: { value: totals[key] } }), {}),
      }}
      actions={["save", "save-close", "close"]}
    />
  );
};

const PropertyBudget: FC<ModalContentProps> = ({
  handleCloseModal,
  id: loanExtensionId,
  budgetChangeAmount,
  riskMessage,
}) => {
  const [isPropertyBudgetDetailsActive, setIsPropertyBudgetDetailsActive] = useState(false);
  const sharedProps = {
    handleCloseModal,
    loanExtensionId,
  };

  return isPropertyBudgetDetailsActive ? (
    <EditPropertyBudgetDetails riskMessage={riskMessage} {...sharedProps} />
  ) : (
    <EditPropertyBudget
      {...sharedProps}
      budgetChangeAmount={budgetChangeAmount}
      onSuccess={() => setIsPropertyBudgetDetailsActive(true)}
    />
  );
};

const setIsBudgetChangingFields = (myForm: BCFormController): void => {
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Budget Changing", {
        field: "checkbox",
        name: CHANGING_SECTIONS.BUDGET,
        inlineRight: true,
      }),
    ],
    { widths: "4" }
  );

  myForm.addGroupFields(
    [
      myForm.getCurrencyField("Modify Budget by", {
        required: true,
        allowNegativeValue: true,
        name: "budgetChangeAmount",
      }),
      myForm.getCurrencyField("Original Budget Amount", { disabled: true }),
      myForm.getCurrencyField("Amended Budget Amount", {
        required: true,
        disabled: true,
      }),
    ],
    { widths: 4, isHiddenFunc: isBudgetHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Budget Changes Completed", {
        field: "checkbox",
        inline: true,
      }),
      myForm.getCalendarField("Requested Effective Budget Increase Date", {
        name: "requestedBudgetChangeEffectiveDate",
        required: true,
      }),
      myForm.getCalendarField("Effective Budget Increase Date", {
        required: true,
        name: "budgetChangeEffectiveDate",
      }),
      myForm.getCalendarField("Budget Change Implemented On", {
        disabled: true,
      }),
    ],
    { isHiddenFunc: isBudgetHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getButtonField(
        "Modify Budget",
        (options) => {
          const {
            inputs: { budgetChangeAmount, formType },
          } = options;
          if (budgetChangeAmount === null) {
            return options.setFormErrorMessage({
              message: "Please enter Budget Change Amount.",
            });
          }
          dispatchOpenModal(formType, ModalContentName.EditPropertyBudget);
        },
        {
          width: 4,
          fullWidth: true,
        }
      ),
    ],
    { isHiddenFunc: isBudgetHidden }
  );
};

const getChangeBudget: GetterFn = (data, options) => {
  const { originalBudgetAmount, remainingBudget } = data;
  let changedBudget = false;
  let budgetChangeAmount =
    data.budgetChangeAmount === null || data.budgetChangeAmount === undefined
      ? null
      : +data.budgetChangeAmount;

  if (options && budgetChangeAmount !== null && budgetChangeAmount < 0) {
    const isBudgetLessOriginal = budgetChangeAmount < originalBudgetAmount * -1;
    if (isBudgetLessOriginal || budgetChangeAmount < remainingBudget * -1) {
      options.setFormErrorMessage({
        message: `You cannot remove more than the ${
          isBudgetLessOriginal ? "original/" : ""
        }remaining budget amount`,
      });
      changedBudget = true;
      budgetChangeAmount = remainingBudget * -1;
    }
  }

  return {
    budgetChangesCompleted: false,
    ...(changedBudget ? { budgetChangeAmount } : {}),
    amendedBudgetAmount: data.originalBudgetAmount + (budgetChangeAmount || 0),
  };
};

const getChangeBudgetPipe: GetterFn = getterPipe(
  getChangeBudget,
  getCalculatedAmendedLoanAmountPipe
);

const getChangeRequestedBudgetChangeEffectiveDate: GetterFn = (data) => ({
  budgetChangeEffectiveDate: data.requestedBudgetChangeEffectiveDate,
});

const getChangeRequestedBudgetChangeEffectiveDatePipe: GetterFn = getterPipe(
  getChangeRequestedBudgetChangeEffectiveDate,
  getExtensionFeeEffectiveDate
);

const getBudgetChangesCompleted: GetterFn = async (data, options) => {
  const { budgetChangeAmount, budgetChangesCompleted, formType, id } = data;
  const handleBudgetNoComplete = (message: string) => {
    options.setFormErrorMessage({ message });
    return { budgetChangesCompleted: false };
  };

  if (budgetChangesCompleted) {
    if (formType === "add" || budgetChangeAmount === null || budgetChangeAmount === undefined) {
      return handleBudgetNoComplete(
        data.formType === "add" ? "Please Modify Budget." : "Please enter a Modify Budget Amount."
      );
    }

    const response = await options.fetchData("/amendments/is-modify-budget-complete", {
      loanExtensionId: id,
      budgetChangeAmount: +budgetChangeAmount,
    });

    if (typeof response.data === "boolean" && !response.data) {
      return handleBudgetNoComplete("Modify Budget is not complete.");
    }
  }
  return {};
};

const handleBudgetChanging: GetterFn = (data) =>
  !data[CHANGING_SECTIONS.BUDGET]
    ? {
        budgetChangeAmount: null,
        budgetChangesCompleted: false,
        budgetChangeEffectiveDate: null,
        requestedBudgetChangeEffectiveDate: null,
      }
    : {};

const handleBudgetChangingPipe: GetterFn = getterPipe(
  handleBudgetChanging,
  getExtensionTypePipe,
  getExtensionFeeEffectiveDate,
  getChangeBudgetPipe
);

const isApplyInterestReserveOptionHidden = ({ inputs }) =>
  isInterestHidden({ inputs }) || !inputs.applyInterestReserveToOutstandingTransactions;

const setIsInterestChangingFields = (myForm: BCFormController): void => {
  myForm.addGroupFields(
    [
      myForm.getInputField("Is Interest Changing", {
        name: CHANGING_SECTIONS.INTEREST,
        field: "checkbox",
        inlineRight: true,
      }),
    ],
    { widths: "4" }
  );

  myForm.addGroupFields(
    [
      myForm.getCurrencyField("Change Amount", {
        allowNegativeValue: true,
        name: "interestReserveChangeAmount",
        required: true,
      }),
      myForm.getCurrencyField("Original Interest Reserve", {
        disabled: true,
        name: "originalInterestReserveAmount",
      }),
      myForm.getCurrencyField("Amended Interest Reserves", {
        disabled: true,
        name: "amendedInterestReserveAmount",
      }),
    ],
    { widths: 4, isHiddenFunc: isInterestHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getBlankField(),
      myForm.getCurrencyField("Current Interest Reserve Balance", {
        disabled: true,
        name: "currentInterestReserveAmount",
      }),
      myForm.getCalendarField("Requested Effective Interest Reserve Date", {
        required: true,
        name: "requestedInterestReserveChangeEffectiveDate",
      }),
      myForm.getCalendarField("Effective Interest Reserve Date", {
        required: true,
        name: "interestReserveChangeEffectiveDate",
      }),
    ],
    { widths: 4, isHiddenFunc: isInterestHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getBlankField({ width: 4 }),
      myForm.getCurrencyField("Total Outstanding:", {
        name: "outstandingAmount",
        noBackground: true,
        disabled: true,
        width: 4,
      }),
      myForm.getCalendarField("Interest Reserve Change Implemented On", {
        width: 4,
        disabled: true,
      }),
    ],
    { isHiddenFunc: isInterestHidden }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Do you want to apply IR to pastdue interest and fees?", {
        name: "applyInterestReserveToOutstandingTransactions",
        field: "checkbox",
        inline: true,
        isDisabledOnFunc: ({ inputs }) =>
          !(isNumber(inputs.interestReserveChangeAmount) && inputs.interestReserveChangeAmount > 0),
      }),
      myForm.getLabelField("Apply Interest Reserve To Option", {
        required: true,
        isHiddenFunc: isApplyInterestReserveOptionHidden,
      }),
      ...["Specific Transactions", "All Outstanding Transactions"].map((label) =>
        myForm.getGroupRadioField(label, {
          isHiddenFunc: isApplyInterestReserveOptionHidden,
        })
      ),
    ],
    {
      widths: 1,
      radioGroupName: "applyInterestReserveToOption",
      isHiddenFunc: isInterestHidden,
    }
  );
};

const getChangeRequestedInterestReserveChangeEffectiveDate: GetterFn = (data) => ({
  interestReserveChangeEffectiveDate: data.requestedInterestReserveChangeEffectiveDate,
});

const getChangeRequestedInterestReserveChangeEffectiveDatePipe: GetterFn = getterPipe(
  getChangeRequestedInterestReserveChangeEffectiveDate,
  getExtensionFeeEffectiveDate
);

const getApplyInterestReserveToOutstandingTransactions: GetterFn = (data) =>
  !data.applyInterestReserveToOutstandingTransactions
    ? {
        applyInterestReserveToOption: null,
      }
    : {};

const getChangeInterestReserveChangeAmount: GetterFn = (data) => {
  const interestReserveChangeAmount = +data.interestReserveChangeAmount;
  const hasInterestValue =
    interestReserveChangeAmount !== null && interestReserveChangeAmount !== undefined;

  return {
    amendedInterestReserveAmount: getSumByReduce(
      data.interestReserveChangeAmount,
      data.originalInterestReserveAmount
    ),
    applyInterestReserveToOutstandingTransactions:
      hasInterestValue && interestReserveChangeAmount > 0
        ? false
        : data.applyInterestReserveToOutstandingTransactions,
  };
};

const getChangeInterestReserveChangeAmountPipe: GetterFn = getterPipe(
  getChangeInterestReserveChangeAmount,
  getCalculatedAmendedLoanAmountPipe,
  getApplyInterestReserveToOutstandingTransactions
);

const isHiddenPDRA = ({ inputs }) => !inputs.partialDeedReleaseApplicable;
const handleInterestChanging: GetterFn = (data) =>
  !data[CHANGING_SECTIONS.INTEREST]
    ? {
        interestReserveChangeAmount: null,
        amendedInterestReserveAmount: null,
        interestReserveChangeEffectiveDate: null,
        requestedInterestReserveChangeEffectiveDate: null,
        applyInterestReserveToOutstandingTransactions: false,
      }
    : {};

const handleInterestChangingPipe: GetterFn = getterPipe(
  handleInterestChanging,
  getExtensionTypePipe,
  getExtensionFeeEffectiveDate,
  getChangeInterestReserveChangeAmount
);

const EditPropertyUnit: FC<ModalContentProps> = ({ handleCloseModal, id: loanExtensionId }) => {
  const unitsUrl = `/amendments/${loanExtensionId}/units`;
  const [{ data: units = [] }] = useFetchData(unitsUrl);
  const myForm = new BCFormController(
    {},
    {
      isCompact: true,
      submitMethod: "PUT",
      clearSuffixesOnSubmit: [unitsUrl],
      submitSuffix: "/amendments/units",
      dataFormatterFunc: (data) =>
        Object.keys(data).map((key) => {
          return {
            ...(units.find((unit) => unit.id === key) || {}),
            ...data[key],
          };
        }),
    }
  );

  myForm.addGroupFields([
    ...(
      [
        ["Property", 3],
        ["Unit", 3],
        ["Override PDR Amount", 2],
        ["PDR Amount", 2],
        ["Recommended PDR Amount", 3],
        ["Approved PDR Amount", 3],
      ] as [string, WidthType][]
    ).map(([label, width]) => myForm.getLabelField(label, { width, wrapLabel: true })),
  ]);

  if (units.length > 0) {
    units.forEach((unit) => {
      const nestedInputsProp = unit.id;
      myForm.addGroupFields([
        myForm.getLabelField(unit.loanPropertyDisplay, {
          width: 3,
          noBackground: true,
          wrapLabel: true,
          fontWeightLight: true,
        }),
        myForm.getLabelField(unit.unitDisplay, {
          width: 3,
          noBackground: true,
          wrapLabel: true,
          fontWeightLight: true,
        }),
        myForm.getCheckboxField("", {
          width: 2,
          nestedInputsProp,
          position: "center",
          name: "overridePartialDeedReleaseAmount",
          value: unit.overridePartialDeedReleaseAmount,
        }),
        myForm.getCurrencyField("", {
          disabled: true,
          width: 2,
          nestedInputsProp,
          name: "partialDeedReleaseAmount",
          value: unit.partialDeedReleaseAmount,
        }),
        myForm.getCurrencyField("", {
          disabled: true,
          width: 3,
          nestedInputsProp,
          name: "recommendedDeedReleaseAmount",
          value: unit.recommendedDeedReleaseAmount,
        }),
        myForm.getCurrencyField("", {
          width: 3,
          nestedInputsProp,
          value: unit.approvedPartialDeedReleaseAmount,
          name: "approvedPartialDeedReleaseAmount",
          isDisabledOnFunc: ({ inputs }) =>
            !inputs[nestedInputsProp].overridePartialDeedReleaseAmount,
        }),
      ]);
    });
  }

  return (
    <LmsFormGenerator
      myForm={myForm}
      handleClose={handleCloseModal}
      actions={["save", "save-close", "close", HELP_AMENDMENT_BUTTON]}
    />
  );
};

const setAmendedDetailsFields = (myForm: BCFormController): void => {
  myForm.addLabelField("Amended Details", {
    isHiddenFunc: isAmendedDetailsHidden,
  });

  [
    [
      myForm.getCurrencyField("Original Loan Amount", {
        disabled: true,
        name: "loanAmountOriginal",
        width: 5,
      }),
      myForm.getCurrencyField("Amended Loan Amount", {
        disabled: true,
        width: 5,
      }),
      myForm.getCurrencyField("Total of All Loan Amount Changes", {
        disabled: true,
        name: "nthAmendedLoanAmount",
        width: 5,
      }),
    ],
    [
      myForm.getInputField("Current LTV", {
        name: "originalLoanToValue",
        width: 5,
        disabled: true,
        decimalsLimit: 2,
        fieldType: "float",
      }),
      myForm.getCheckboxField("Partial Deed Release Applicable", {
        disabled: true,
        inline: true,
        width: 5,
        keepHiddenPosition: true,
        isHiddenFunc: isHiddenPDRA,
      }),
      myForm.getDropdownDisplayField("Partial Deed Release Option", {
        label: "",
        width: 4,
      }),
      myForm.getButtonField(
        "Modify Unit PDR",
        async (options) => {
          const { setIsFormLoading, fetchData } = options;
          setIsFormLoading(true);
          try {
            await fetchData(`/amendments/${options.inputs.id}/modify-unit`, {
              fetchMethod: "PUT",
            });
          } finally {
            setIsFormLoading(false);
          }
          dispatchOpenModal(options.inputs.formType, ModalContentName.EditPropertyUnit);
        },
        {
          width: 2,
          fullWidth: true,
        }
      ),
    ],
    [
      myForm.getInputField("Amended Loan To Value", {
        width: 5,
        disabled: true,
        decimalsLimit: 2,
        fieldType: "float",
        name: "amendedLoanToValue",
      }),
      myForm.getCheckboxField("Override PDR Amount", {
        name: "overridePartialDeedReleaseAmount",
        inline: true,
        width: 5,
        isHiddenFunc: isHiddenPDRA,
        keepHiddenPosition: true,
        isDisabledOnFunc: ({ inputs }) => inputs.amendedLoanAmount === inputs.loanAmountOriginal,
      }),
      myForm.getCurrencyField("PDR Amount", {
        disabled: true,
        name: "partialDeedReleaseAmount",
        width: 5,
        isHiddenFunc: isHiddenPDRA,
        keepHiddenPosition: true,
      }),
    ],
    [
      myForm.getDisplayField("Underwritten LTV", {
        width: 5,
        disabled: true,
      }),
      myForm.getCurrencyField("Recommended PDR Amount", {
        width: 5,
        disabled: true,
        keepHiddenPosition: true,
        isHiddenFunc: isHiddenPDRA,
        name: "recommendedDeedReleaseAmount",
      }),
      myForm.getCurrencyField("Approved PDR Amount", {
        width: 5,
        keepHiddenPosition: true,
        isHiddenFunc: isHiddenPDRA,
        name: "approvedPartialDeedReleaseAmount",
        isDisabledOnFunc: ({ inputs }) => !inputs.overridePartialDeedReleaseAmount,
      }),
    ],
    [
      myForm.getDisplayField("Amended LTV Warning", {
        width: 15,
        name: "amendedLtvMessage",
      }),
    ],
  ].forEach((fields) => {
    myForm.addGroupFields(fields, { isHiddenFunc: isAmendedDetailsHidden });
  });
};

const isHiddenModification = ({ inputs }) => !inputs.modificationFeeBasedOptions;
const setFeeTermsFields = (myForm: BCFormController): void => {
  myForm.addLabelField("What are the fee terms");

  myForm.addGroupFields(
    [
      myForm.getLabelField("Payment Method", { required: true }),
      ...["Borrower Pays", "Paid at Payoff"].map((label) => myForm.getGroupRadioField(label)),
      myForm.getBlankField({ width: 2 }),
      myForm.getCheckboxField("Prorate Fee At Payoff", {
        inline: true,
      }),
    ],
    { radioGroupName: "paymentMethodOption", widths: 1 }
  );

  myForm.addGroupFields(
    [
      myForm.getLabelField("Fee Based Off", { required: true }),
      ...[
        {
          options: ["Original Committed Loan Amount", "Current Balance"],
        },
        {
          options: MODIFICATION_FEE_BASED_OPTIONS,
          isHiddenFunc: isHiddenModification,
        },
      ].reduce<any>(
        (acc, { options, isHiddenFunc }) => [
          ...acc,
          ...options.map((label) => myForm.getGroupRadioField(label, { isHiddenFunc })),
        ],
        []
      ),
    ],
    { radioGroupName: "feeBasedOffOption", widths: 1 }
  );

  myForm.addGroupFields(
    [
      myForm.getInputField("Fee %", {
        fieldType: "percent",
        name: "extensionFeePercentage",
        required: {
          feeBasedOffOption: {
            value: "Flat Fee",
            operator: "notequal",
          },
        },
        isDisabledOnFunc: ({ inputs }) =>
          ((!inputs.feeBasedOffOption || inputs.feeBasedOffOption === "Flat Fee") &&
            !!inputs.originalExtensionFeePercentage) ||
          inputs.feeBasedOffOption === "Flat Fee",
      }),
      myForm.getInputField("Fee Amount", {
        name: "extensionFeeAmount",
        fieldType: "dollar",
        required: {
          feeBasedOffOption: "Flat Fee",
        },
        isDisabledOnFunc: ({ inputs }) =>
          (!inputs.feeBasedOffOption && !!inputs.originalExtensionFeeAmount) ||
          (inputs.feeBasedOffOption !== "Flat Fee" && !!inputs.originalExtensionFeeAmount) ||
          inputs.feeBasedOffOption !== "Flat Fee",
      }),
      myForm.getCalendarField("Requested Effective Fee Date", {
        name: "requestedExtensionFeeEffectiveDate",
        required: {
          requiredRequestedExtensionFeeEffectiveDate: true,
        },
      }),
      myForm.getCalendarField("Effective Fee Date", {
        name: "extensionFeeEffectiveDate",
        required: {
          requiredExtensionFeeEffectiveDate: true,
        },
      }),
    ],
    { widths: 4 }
  );
  myForm.addGroupFields([
    myForm.getBlankField({ width: 4 }),
    myForm.getCurrencyField("Other Fees", { width: 4, disabled: true }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Original Loan Amount", {
      name: "loanAmountOriginal",
      disabled: true,
      noBackground: true,
      width: 4,
    }),
    myForm.getCurrencyField("Current Loan Amount", {
      name: "loanAmountCurrent",
      disabled: true,
      noBackground: true,
      width: 4,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getCurrencyField("Amendment Loan Amount", {
      name: "amendedLoanAmount",
      disabled: true,
      noBackground: true,
      width: 4,
    }),
    myForm.getCurrencyField("Current Loan + Fees", {
      disabled: true,
      name: "currentLoanPlusFees",
      width: 4,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getBlankField({ width: 4 }),
    myForm.getCurrencyField("Over by", { disabled: true, width: 4 }),
  ]);
};

const getChangeRequestedExtensionFeeEffectiveDate: GetterFn = (data) => ({
  extensionFeeEffectiveDate: data.requestedExtensionFeeEffectiveDate,
});

const AmendmentDocumentForm = ({ handleClose, loanExtensionId }) => {
  const form = new BCFormController(
    {},
    {
      submitSuffix: `/loandetails/servicing/add-document/${loanExtensionId}`,
      payloadDataType: "FormData",
    }
  );

  form.addCenteredGroup([
    form.getDropdownField("Document Master", "metadata/doc-masters", {
      required: true,
    }),
  ]);

  form.addCenteredGroup([form.getFileField("Document")]);

  return (
    <LmsFormGenerator myForm={form} actions={["save-close", "close"]} handleClose={handleClose} />
  );
};

const isDisabledByChangeDecision = ({ inputs }) => !!inputs.changeDecision;

const setReviewFields = (myForm: BCFormController, data): void => {
  myForm.addLabelField("Review");
  myForm.addGroupFields([
    myForm.getDropdownField("Amendment Type", EXTENSION_DROPDOWN_ENDPOINTS.TYPES, {
      required: true,
      disabled: true,
      name: "extensionTypeId",
    }),
    myForm.getBlankField(),
    myForm.getCalendarField("Executed Loan Amendment Date"),
    myForm.getInputField("Executed Loan Amendment Agreement", {
      inline: true,
      field: "checkbox",
      position: "right-center",
      name: "isExecutedLoanAmendmentAgreement",
      keepHiddenPosition: true,
      isHiddenFunc: isHiddenModification,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("Extension Reason", {
      width: 8,
      required: true,
      isDisabledOnFunc: ({ inputs }) => !!inputs.originalExtensionReason,
    }),
    myForm.getCalendarField("Amendment Signature Received Date", {
      width: 4,
      name: "extensionSignatureReceivedDate",
    }),
    myForm.getInputField("First Amendment Recorded", {
      width: 4,
      inline: true,
      field: "checkbox",
      position: "right-center",
      keepHiddenPosition: true,
      isHiddenFunc: isHiddenModification,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getTextareaField("Review Comments", { width: 8 }),
    myForm.getCalendarField("Received DOT Amendment Date", {
      name: "receivedDotamendmentDate",
      width: 4,
    }),
    myForm.getInputField("Title Updated", {
      width: 4,
      inline: true,
      field: "checkbox",
      position: "right-center",
      keepHiddenPosition: true,
      isHiddenFunc: isHiddenModification,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getDropdownField("Review Decision", EXTENSION_DROPDOWN_ENDPOINTS.DECISIONS, {
      required: true,
      name: "extensionDecisionId",
      isDisabledOnFunc: isDisabledByChangeDecision,
    }),
    myForm.getBlankField(),
    myForm.getInputField("Amendment Signature Received", {
      name: "extensionSignatureReceived",
      field: "checkbox",
      inline: true,
    }),
    myForm.getInputField("LMS Updated", {
      name: "lmsupdated",
      field: "checkbox",
      position: "right-center",
      inline: true,
      keepHiddenPosition: true,
      isHiddenFunc: isHiddenModification,
    }),
  ]);

  myForm.addGroupFields([
    myForm.getCalendarField("Review Date", { disabled: true }),
    myForm.getBlankField(),
    myForm.getInputField("Manual Delivery", {
      inline: true,
      field: "checkbox",
    }),
    myForm.getInputField("Budget Updated", {
      inline: true,
      field: "checkbox",
      position: "right-center",
      keepHiddenPosition: true,
      isHiddenFunc: isHiddenModification,
    }),
  ]);

  myForm.addGroupFields([myForm.getDisplayField("LRC Comments", { width: 4 })]);
  myForm.addGroupFields([myForm.getDisplayField("Loan Purchaser Decision", { width: 4 })]);
  myForm.addGroupFields([
    myForm.getDropdownField("Amendment Status", EXTENSION_DROPDOWN_ENDPOINTS.STATUSES, {
      name: "extensionStatusId",
      width: 4,
      required: true,
      isDisabledOnFunc: isDisabledByChangeDecision,
    }),
  ]);

  myForm.addDividerField();
  myForm.addLabelField("Amendment Comments");
  myForm.addGroupFields([
    myForm.getButtonField("View Comments", () => null, {
      width: 4,
      fullWidth: true,
      modalContent: (_, options) => (
        <LmsLoanCommentsTable loanId={options.inputs.loanId} isReadOnly />
      ),
    }),
    myForm.getLabelField("Subject", { width: 3, position: "center" }),
    myForm.getLabelField("Description", { width: 3, position: "center" }),
    myForm.getLabelField("Created On", { width: 3, position: "center" }),
    myForm.getLabelField("Created By", { width: 3, position: "center" }),
  ]);

  if (data && Array.isArray(data.loanComments) && data.loanComments.length > 0) {
    data.loanComments.forEach((loanComment: any) => {
      myForm.addGroupFields([
        myForm.getBlankField({
          width: 4,
        }),
        myForm.getLabelField(loanComment.commentSubject.commentCategorySubjectDisplay, {
          width: 3,
          wrapLabel: true,
          position: "center",
          fontWeightLight: true,
        }),
        myForm.getLabelField(loanComment.description, {
          width: 3,
          wrapLabel: true,
          position: "center",
          fontWeightLight: true,
        }),
        myForm.getLabelField(loanComment.createdOn.split("T")[0], {
          width: 3,
          wrapLabel: true,
          position: "center",
          fontWeightLight: true,
        }),
        myForm.getLabelField(loanComment.createdBy, {
          width: 3,
          wrapLabel: true,
          position: "center",
          fontWeightLight: true,
        }),
      ]);
    });
  }
};

const loanExtensionFields = (myForm: BCFormController, data): BCFormController => {
  myForm.addGroupFields(
    [
      ...[
        ["Borrower", "borrowerOrganization"],
        ["Special Purpose Entity", "specialPurposeEntity"],
      ].map(([label, name]) => myForm.getDisplayField(label, { name })),
      myForm.getDropdownField("Loan", LmsEndpoints.Loans.GET.LIST_LOANS, {
        required: true,
        disabled: true,
      }),
      myForm.getCurrencyField("Original Loan Amount", {
        disabled: true,
        name: "loanAmountOriginal",
      }),
    ],
    { widths: 4 }
  );

  [
    setIsTermChangingFields,
    setIsRateChangingFields,
    setIsBudgetChangingFields,
    setIsInterestChangingFields,
    setAmendedDetailsFields,
    setFeeTermsFields,
    setReviewFields,
  ].forEach((setSectionFields) => {
    myForm.addDividerField();
    setSectionFields(myForm, data);
  });

  myForm.addDividerField();
  myForm.addGroupFields([
    myForm.getButtonField("Add Document", () => null, {
      width: 4,
      fullWidth: true,
      modalContent(setIsAddFormModalOpen, options) {
        return (
          <AmendmentDocumentForm
            loanExtensionId={options.inputs.id}
            handleClose={() => setIsAddFormModalOpen(false)}
          />
        );
      },
    }),
  ]);

  setRiskMessage(myForm);
  return myForm;
};

const getInitialData = async (data, formType: FormType, fetchData) => {
  const initialData = {
    formType,
    id: data.id === "00000000-0000-0000-0000-000000000000" ? guid() : data.id,
    originalExtensionReason: data.extensionReason,
    originalExtensionFeeAmount: data.extensionFeeAmount,
    originalExtensionFeePercentage: data.extensionFeePercentage,
    ...getRequiredFeeDates(data),
    ...getFeeBasedOffOptions(data),
    ...(await getterPipe(
      getCalculatedAmendedLoanAmount,
      getCurrentLoanPlusFee,
      getLTVMessage,
      formType === "edit" ? getAmendedDetails : () => ({ changeDecision: true }),
      getOverBy
    )(data, { fetchData, setIsFormLoading: () => null })),
  };

  if (FeatureFlags.IS_DEBUG) {
    console.log("new initial data", initialData);
  }

  return initialData;
};

const FIELD_HANDLERS = {
  [CHANGING_SECTIONS.TERM]: handleTermChangingPipe,
  extensionDays: getProposedDatePipe,
  [CHANGING_SECTIONS.RATE]: handleRateChangingPipe,
  newInterestRate: getNewInterestRatePipe,
  requestedRateChangeEffectiveDate: getRateChangeDatesPipe,
  [CHANGING_SECTIONS.BUDGET]: handleBudgetChangingPipe,
  budgetChangeAmount: getChangeBudgetPipe,
  budgetChangesCompleted: getBudgetChangesCompleted,
  requestedBudgetChangeEffectiveDate: getChangeRequestedBudgetChangeEffectiveDatePipe,
  budgetChangeEffectiveDate: getExtensionFeeEffectiveDate,
  [CHANGING_SECTIONS.INTEREST]: handleInterestChangingPipe,
  interestReserveChangeAmount: getChangeInterestReserveChangeAmountPipe,
  requestedInterestReserveChangeEffectiveDate:
    getChangeRequestedInterestReserveChangeEffectiveDatePipe,
  interestReserveChangeEffectiveDate: getExtensionFeeEffectiveDate,
  applyInterestReserveToOutstandingTransactions: getApplyInterestReserveToOutstandingTransactions,
  feeBasedOffOption: getChangeFeeBasedOffOption,
  extensionFeePercentage: getChangeExtensionFeePercentagePipe,
  extensionFeeAmount: getChangeExtensionFeeAmountPipe,
  requestedExtensionFeeEffectiveDate: getChangeRequestedExtensionFeeEffectiveDate,
};

const handleLoanExtensionChange =
  (setLoanExtensionState) =>
  async (options, field?: string): Promise<void> => {
    const { setInputs, inputs } = options;
    if (field && field in FIELD_HANDLERS) {
      const newData = await FIELD_HANDLERS[field](inputs, options);

      if (FeatureFlags.IS_DEBUG) {
        console.log(`field: ${field}`, newData);
      }

      setInputs((prevData) => {
        const newState = {
          ...prevData,
          ...newData,
        };

        setLoanExtensionState(newState);
        return newState;
      });
    }
  };

const LoanExtensionModalContent = ({
  modalContentName,
  loanExtensionData,
  handleCloseModal,
}: {
  modalContentName: ModalContentName;
  loanExtensionData: GenericProps;
  handleCloseModal: () => void;
}) => {
  let Component: FC<ModalContentProps> | null = null;
  switch (modalContentName) {
    case ModalContentName.EditPropertyUnit:
      Component = EditPropertyUnit;
      break;
    case ModalContentName.EditPropertyBudget:
      Component = PropertyBudget;
      break;
  }

  return Component !== null ? (
    <Component {...loanExtensionData} handleCloseModal={handleCloseModal} />
  ) : null;
};

const LoanExtensionForm: FC<LoanExtensionFormProps> = ({ loanId, type, extensionId }) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [modalContentName, setModalContentName] = useState<ModalContentName | null>(null);
  const amendmentUrl = `/amendments/${type === "add" ? `initial-data/${loanId}` : extensionId}`;
  const [{ data = {}, isLoading, error, fetchData }, callApiNow] = useFetchData(amendmentUrl, {
    isReady: false,
  });

  const [loanExtensionState, setLoanExtensionState] = useState<any>();
  const hasRiskLevel =
    loanExtensionState &&
    ["low", "medium", "high"].includes((loanExtensionState.riskLevel || "").toLowerCase());

  const myForm = loanExtensionFields(
    new BCFormController(loanExtensionState, {
      values: loanExtensionState,
      submitSuffix: "amendments",
      submitMethod: type === "add" ? "POST" : "PUT",
      clearSuffixesOnSubmit: type !== "add" ? [amendmentUrl] : [],
      initIsDisabled: type === "edit" && STATUS_IDS_DISABLE_FORM.includes(data.extensionStatusId),
    }),
    loanExtensionState
  );

  const handleCloseConfirm = (): void => setIsConfirmOpen(false);
  const handleConfirm = (): void => {
    handleCloseConfirm();
    const btn: HTMLElement | null = document.querySelector(`.${ADD_SAVE_BTN_CLASS_NAME}`);
    if (btn) {
      btn.click();
    }
  };

  useEffect(() => {
    const handleOpenModal = (event): void => {
      const { formType, modalName } = event.detail;
      if (formType === "add") {
        return setIsConfirmOpen(true);
      }
      setModalContentName(modalName);
    };

    window.addEventListener(OPEN_MODAL_EVENT_NAME, handleOpenModal);
    return () => window.removeEventListener(OPEN_MODAL_EVENT_NAME, handleOpenModal);
  }, []);

  useEffect(() => {
    callApiNow();
  }, [type]);

  useEffect(() => {
    (async () => {
      if (data.id) {
        const state = {
          ...data,
          ...(await getInitialData(data, type, fetchData)),
        };

        setLoanExtensionState(state);
      }
    })();
  }, [data.id]);

  const handleCloseModal = (): void => {
    setModalContentName(null);
  };

  return (
    <>
      <Confirm
        size="tiny"
        onConfirm={handleConfirm}
        onCancel={handleCloseConfirm}
        content={CREATE_AMENDMENT_MSG}
        open={type === "add" && isConfirmOpen}
      />
      <LmsFormGenerator
        error={error}
        myForm={myForm}
        isLoading={isLoading}
        onFormChanged={handleLoanExtensionChange(setLoanExtensionState)}
        componentProps={{
          riskMessage:
            loanExtensionState && loanExtensionState.riskMessage
              ? { children: loanExtensionState.riskMessage }
              : {},
        }}
        actions={[
          ...(type === "add"
            ? [
                {
                  action: "save-continue",
                  text: "Save",
                  className: ADD_SAVE_BTN_CLASS_NAME,
                },
              ]
            : ["save", "save-close"]),
          ...(loanExtensionState && loanExtensionState.enableLrc && !hasRiskLevel
            ? [
                {
                  text: "Send To LRC",
                  icon: GlobalConstants.Icons.SAVE,
                },
              ]
            : []),
          {
            text: "Generate Note Amendment",
            action: "download",
            path: `loandetails/amendment/${extensionId}/download-note-amendment?newInterestRate=${
              loanExtensionState ? loanExtensionState.newInterestRate || "" : ""
            }`,
            icon: GlobalConstants.Icons.EXCEL_OUTLINE,
            isDisabled: () => type === "add",
          },
          "close",
          HELP_AMENDMENT_BUTTON,
        ]}
      />
      {type === "edit" && modalContentName !== null && loanExtensionState ? (
        <BCModal
          isModalOpen
          closeIcon
          size="fullscreen"
          onClose={handleCloseModal}
          content={
            <LoanExtensionModalContent
              modalContentName={modalContentName}
              loanExtensionData={loanExtensionState}
              handleCloseModal={handleCloseModal}
            />
          }
        />
      ) : null}
    </>
  );
};

const handleFormChanged = async (options, field) => {
  const {
    inputs: { borrowerOrganizationId, specialPurposeEntityId, loanId },
    setAllDropdownOptionsData,
    setInputs,
    fetchData,
    setIsFormLoading,
  } = options;

  const setNewValues = (values) => setInputs((prevInputs) => ({ ...prevInputs, ...values }));

  const handleFetch = async (url: string, defaultData: unknown = null) => {
    try {
      setIsFormLoading(true);
      if (!url) return defaultData;
      const res = await fetchData(url);
      if (res.data) return res.data;
      return defaultData;
    } finally {
      setIsFormLoading(false);
    }
  };

  const handleFetchDropdownOption = async (inputName: string, url: string) => {
    const options = getDropdownSemanticOptionsFromData(await handleFetch(url, []));

    setAllDropdownOptionsData((prevOptions) => ({
      ...prevOptions,
      [inputName]: options,
    }));
  };

  const setLoanOptions = (params = { borrowerOrganizationId, specialPurposeEntityId }) => {
    handleFetchDropdownOption(
      "loanId",
      appendParamsToUrl(EXTENSION_DROPDOWN_ENDPOINTS.SERVICING_LOANS, params)
    );
  };

  switch (field) {
    case "borrowerOrganizationId": {
      setLoanOptions();
      setNewValues({ loanId: null, specialPurposeEntityId: null });
      handleFetchDropdownOption(
        "specialPurposeEntityId",
        borrowerOrganizationId ? `orgs/list-spes/${borrowerOrganizationId}` : ""
      );

      break;
    }
    case "specialPurposeEntityId": {
      setLoanOptions();
      setNewValues({ loanId: null });
      break;
    }
    case "loanId": {
      const defaultValues = {
        borrowerOrganizationId: null,
        specialPurposeEntityId: null,
      };

      if (!loanId) setLoanOptions(defaultValues);
      const data = await handleFetch(loanId ? `/loans/${loanId}/details` : "", defaultValues);

      if (data.borrowerOrganizationId) {
        handleFetchDropdownOption(
          "specialPurposeEntityId",
          `orgs/list-spes/${data.borrowerOrganizationId}`
        );
      }

      setNewValues({
        loanPropertyId: null,
        borrowerOrganizationId: data.borrowerOrganizationId,
        specialPurposeEntityId: data.specialPurposeEntityId,
      });

      break;
    }
  }
};

export const SelectLoanForm = ({ history }) => {
  const myForm = new BCFormController(
    {},
    { errorTitle: "Only Loans with Loan Phase Servicing will be displayed" }
  );

  [
    myForm.getDropdownField("Borrower", EXTENSION_DROPDOWN_ENDPOINTS.BORROWER, {
      required: true,
      name: "borrowerOrganizationId",
    }),
    myForm.getDropdownField("Special Purpose Entity", []),
    myForm.getDropdownField("Loan", EXTENSION_DROPDOWN_ENDPOINTS.SERVICING_LOANS, {
      required: true,
    }),
  ].map((field) => myForm.addCenteredGroup([field]));

  const handleContinue = (inputs) => {
    history.replace(`/servicing-amendments/add?${LOAN_ID_PARAM}=${inputs.loanId}`);
  };

  return (
    <LmsFormGenerator
      myForm={myForm}
      handleSubmit={handleContinue}
      onFormChanged={handleFormChanged}
      actions={[
        {
          icon: GlobalConstants.Icons.CHEVRON_RIGHT,
          text: "Next",
          action: "save",
          handleClick: handleContinue,
        },
        "close",
      ]}
    />
  );
};

export const AmendmentsTable = ({ suffix = "", servicingData = {}, isLoanDetails = false }) => {
  const [isAddLoanDetailsAmendmentsModalOpen, setIsAddLoanDetailsAmendmentsModalOpen] =
    useState(false);
  return (
    <>
      <BCModal
        isModalOpen={isAddLoanDetailsAmendmentsModalOpen}
        size="large"
        closeIcon
        onClose={() => setIsAddLoanDetailsAmendmentsModalOpen(false)}
        content={
          <AddNewBuiltInAmendmentForm
            handleAddAmendmentClose={() => setIsAddLoanDetailsAmendmentsModalOpen(false)}
            loanData={servicingData}
          />
        }
      />
      <LmsTable
        rowsBackgroundCustomColor={(rowData) => (rowData.frozen === "Yes" ? "cyan" : "")}
        columnLabelsDictionary={{
          loanNumber: "Loan #",
          extensionType: "Amendment Type",
          extensionDate: "Amendment Date",
          extensionDays: "Amendment Days",
          extensionFeePercentage: "Fee %",
          extensionFeeAmount: "Fee Amount",
          extensionRateChangePercentage: "Rate Change %",
          extensionStatus: "Amendment Status",
          applicationNumber: "Application #",
          extensionDecision: "Review Decision",
          extensionSignatureReceived: "Amendment Signature Received",
          lmsupdated: "LMS Updated",
          daewaitingForApproval: "DAE Waiting For Approval",
          daeapprovedOn: "DAE Approved On",
          daedeclinedOn: "DAE Declined On",
          brms: "BRMs",
          initialCos: "Initial COs",
          reviewCos: "Review COs",
          newInterestRate: "Revised Rate",
          extensionNumber: "Amendment #",
        }}
        apiSuffix={suffix ? suffix : "amendments"}
        actionButtons={[
          isLoanDetails
            ? {
                text: "Add",
                handleClick: () => setIsAddLoanDetailsAmendmentsModalOpen(true),
              }
            : "add",
          "edit",
          "export",
          DECLINE_AMENDMENT_BUTTON,
        ]}
        sortable={["Created On"]}
        columnClickOptions={{
          applicationNumber: {
            path: "/servicing-amendments/{id}",
            idProp: "id",
          },
        }}
      />
    </>
  );
};

const LmsServicingAmendments = ({ params = {}, history }) => {
  const { isMainPage, isAdd, isEdit, isActionAllowed, isView, id, query } = useParseRouteActions(
    params,
    ["add", "edit"]
  );

  const loanId = query.get(LOAN_ID_PARAM);

  if (!isActionAllowed) {
    history.goBack();
  }

  return (
    <>
      {isMainPage && <AmendmentsTable />}
      {(isAdd || isEdit || isView) &&
        (isAdd && !loanId ? (
          <SelectLoanForm history={history} />
        ) : (
          <LoanExtensionForm extensionId={id} loanId={loanId} type={isAdd ? "add" : "edit"} />
        ))}
    </>
  );
};

export default LmsServicingAmendments;
